import type * as angular from 'angular';
import type {Observable} from 'rxjs';
import { Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {isFunction} from 'shared/utils/custom-utils';

/**
 * @ngdoc provider
 * @name searchInputServiceProvider
 * @module portaro.components.search-input
 *
 * @description
 * Provider for setting  custom functions for correct functioning of {@link component:kpMainMenu}
 *
 * ## example
 * ```js
 * angular.config(
 *      ['searchInputServiceProvider', (searchInputServiceProvider) => {
 *           searchInputServiceProvider.setSearchFunction((searchPhrase) => {
 *              console.log('Search: ', searchPhrase);
 *           });
 *
 *           searchInputServiceProvider.setAutocompleteSearchFunction((searchPhrase, $httpParamSerializer) => {
 *              const params = {
 *                  searchPhrase
 *              };
 *
 *              return ajax({
 *                          method: 'get',
 *                          url: `api/searched-terms?${$httpParamSerializer(params)}`
 *                      });
 *           });
 *      }]
 * );
 * ```
 */

/**
 * @ngdoc service
 * @name searchInputService
 * @module portaro.components.search-input
 *
 * @description
 * Service for {@link component:kpSearchInput kp-search-input component}
 */

export type SearchFunction = (searchPhrase?: string, ...args: any[]) => void;
export type AutocompleteSearchFunction = (searchPhrase?: string, ...args: any[]) => Observable<string[]>;

export interface SearchInputService {

    /**
     * @ngdoc method
     * @name searchInputService#isEnabled
     */
    isEnabled(): boolean;

    // TODO
    /**
     * @ngdoc method
     * @name searchInputService#updateSearchValue
     *
     * @description
     *
     * @param {string} searchValue
     */
    updateSearchValue(searchValue: string): void;

    // TODO
    /**
     * @ngdoc method
     * @name searchInputService#getAutocompleteValuesStream
     *
     * @description
     *
     * @returns {Observable<string[]>} Stream with autocomplete values.
     */
    getAutocompleteValuesStream(): Observable<string[]>;

    // TODO
    /**
     * @ngdoc method
     * @name searchInputService#getCurrentSearchPhraseStream
     *
     * @description
     *
     * @returns {Observable<string>} currentSearchPhrase stream.
     */
    getCurrentSearchPhraseStream(): Observable<string>;

    // TODO
    /**
     * @ngdoc method
     * @name searchInputService#getSearchValue
     *
     * @description
     *
     * @returns {string}
     */
    getSearchValue(): string;

    // TODO
    /**
     * @ngdoc method
     * @name searchInputService#setSearchValue
     *
     * @description
     *
     * @param {string} searchValue
     */
    setSearchValue(searchValue: string): void;

    // TODO
    /**
     * @ngdoc method
     * @name searchInputService#triggerSearch
     *
     * @description
     *
     * @param {string=} forceSearchValue
     */
    triggerSearch(forceSearchValue?: string): void;
}

export default class SearchInputServiceProvider {
    public static providerName = 'searchInputService';

    public readonly $get: ($injector: angular.auto.IInjectorService, GLOBAL_SEARCH_INPUT_ENABLED: boolean) => SearchInputService;

    private searchFunction: SearchFunction = () => null;
    private autocompleteSearchFunction: AutocompleteSearchFunction = () => null;

    private searchPhrase$ = new Subject<string>();
    private autocompleteValues$: Observable<string[]>;
    private currentSearchPhrase$: Observable<string>;

    private searchValue: string;

    constructor() {
        this.currentSearchPhrase$ = this.searchPhrase$.pipe();
        this.$get = /*@ngInject*/ ($injector: angular.auto.IInjectorService, GLOBAL_SEARCH_INPUT_ENABLED: boolean) => this.createSearchInputService($injector, GLOBAL_SEARCH_INPUT_ENABLED);
    }

    /**
     * @ngdoc method
     * @name searchInputServiceProvider#setSearchFunction
     *
     * @description
     * Set function which is triggered when user performs search submitting of search from.
     *
     * @param {function(*)} searchFunction Function, which is triggered if user performs search.
     * Function supports dependency injection through function arguments. Search phrase is accessible via `searchPhrase` argument.
     */
    public setSearchFunction(searchFunction: SearchFunction) {
        if (isFunction(searchFunction)) {
            this.searchFunction = searchFunction;
        } else {
            throw new TypeError(`Invalid argument type. Function required, got: ${searchFunction}`);
        }
    }

    /**
     * @ngdoc method
     * @name searchInputServiceProvider#setAutocompleteSearchFunction
     *
     * @description
     * Set function which returns data for autocomplete in search input.
     *
     * @param {function(*): Observable<string[]>} autocompleteSearchFunction Function, which is triggered if user performs search.
     * Function supports dependency injection through function arguments. Search phrase is accessible via `searchPhrase` argument.
     */
    public setAutocompleteSearchFunction(autocompleteSearchFunction: AutocompleteSearchFunction) {
        if (isFunction(autocompleteSearchFunction)) {
            this.autocompleteSearchFunction = autocompleteSearchFunction;
        } else {
            throw new TypeError(`Invalid argument type. Function required, got: ${autocompleteSearchFunction}`);
        }
    }

    private setupAutocompleteStream($injector: angular.auto.IInjectorService) {
        this.autocompleteValues$ = this.searchPhrase$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((searchPhrase) => $injector.invoke(this.autocompleteSearchFunction, this, {searchPhrase}))
        );
    }

    private createSearchInputService($injector: angular.auto.IInjectorService, GLOBAL_SEARCH_INPUT_ENABLED: boolean): SearchInputService {
        this.searchValue = undefined;

        this.setupAutocompleteStream($injector);

        return {
            isEnabled: () => GLOBAL_SEARCH_INPUT_ENABLED,

            updateSearchValue: (searchValue: string) => {
                this.searchValue = searchValue;
                this.searchPhrase$.next(searchValue);
            },

            getCurrentSearchPhraseStream: () => {
                return this.currentSearchPhrase$;
            },

            getAutocompleteValuesStream: () => this.autocompleteValues$,

            getSearchValue: () => this.searchValue,

            setSearchValue: (searchValue) => {
                this.searchValue = searchValue;
            },

            triggerSearch: (forceSearchValue?: string) => {
                if (isFunction(this.searchFunction)) {
                    $injector.invoke(this.searchFunction, this, {searchPhrase: forceSearchValue || this.searchValue});
                } else {
                    throw new TypeError(`Invalid function: ${this.searchFunction}`);
                }
            }
        };
    }
}
